import React, { useState, useMemo, useRef } from 'react';
import {
  Card,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Stack,
  Alert,
} from '@mui/material';
import JoditEditor from 'jodit-react';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import SnackbarInfo from '../common/snackbar-info.component';
import { EMAIL_PLACEHOLDERS } from '../../config/const.config';
import ComposeEmailPreview from './compose-email-preview.component';
import Recipients from '../common/recipients.component';
import { createEmailCampaign } from '../../services/email-campaigns.service';

const BulkEmailCompose = () => {
  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      fromEmail: 'no-reply@torahclass.com',
      fromName: 'Torah Class',
      subject: '',
      message: '',
    },
  });

  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const editor = useRef(null);
  const editorConfig = useMemo(
    () => ({
      readonly: false,
      height: 400,
      toolbar: true,
      placeholder: 'Start typing your email...',
      buttons: [
        'bold',
        'italic',
        'underline',
        'strikethrough',
        '|',
        'font',
        'fontsize',
        'brush',
        'paragraph',
        '|',
        'ul',
        'ol',
        'align',
        '|',
        'link',
        'image',
        'table',
        '|',
        'undo',
        'redo',
        'source',
      ],
    }),
    []
  );

  const handleCopyPlaceholder = (text) => {
    setSnackbarInfo({ ...snackbarInfo, show: false });

    navigator.clipboard.writeText(`{{${text}}}`);
    setTimeout(() => {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'info',
        message: 'Text copied to clipboard.',
      });
    }, 100);
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({ ...snackbarInfo, show: false });

    const payload = {
      senderEmail: data.fromEmail?.trim() || '',
      senderName: data.fromName?.trim() || '',
      subject: data.subject?.trim() || '',
      message: data.message?.trim() || '',
      recipients: selectedRecipients.map((item) => item.id),
    };
    createEmailCampaign(payload)
      .then(() => {
        setSnackbarInfo({
          show: true,
          type: 'success',
          message: 'Bulk Email process intiated.',
        });
        setSelectedRecipients([]);
        setFormSubmitted(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  return (
    <>
      <form id="bulk-email-compose-form" onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <Alert severity="info" icon={<Iconify icon="ic:outline-info" />}>
              Use this tool to send bulk emails to subscribers.
            </Alert>
          </Grid>

          <Grid item xs={12} sm={4} md={4}>
            <Card sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Recipients
              </Typography>
              <Recipients
                id="recipients"
                name="recipients"
                label="Recipients"
                defaultValue={selectedRecipients}
                onChange={(ids) => setSelectedRecipients(ids)}
              />
            </Card>
          </Grid>
          <Grid item xs={12} sm={8} md={8}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Compose Email
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    fullWidth
                    label="From Email"
                    value={getValues('fromEmail')}
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    fullWidth
                    label="From Name"
                    value={getValues('fromName')}
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                  <Typography variant="body2" color="inherit">
                    Use following placeholders to personalize email content.
                    Click on tags to copy and then paste it in subject and/or
                    content fields
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                  <Box display="flex" gap={1}>
                    {EMAIL_PLACEHOLDERS.map((item) => (
                      <Button
                        variant="outlined"
                        size="small"
                        color="inherit"
                        onClick={() => handleCopyPlaceholder(item.value)}
                        sx={{ minWidth: 'auto' }}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                  <Controller
                    name="subject"
                    control={control}
                    rules={{
                      ...validationRules.REQUIRED,
                      ...maxLengthValidation(255),
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Subject"
                        error={!!errors.subject}
                        helperText={errors?.subject?.message || null}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                  <Controller
                    name="message"
                    control={control}
                    rules={validationRules.REQUIRED}
                    render={({ field }) => (
                      <>
                        <JoditEditor
                          ref={editor}
                          config={editorConfig}
                          value={field.value}
                          onBlur={(newContent) => field.onChange(newContent)}
                        />
                        {errors?.message?.message && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 1, display: 'block' }}
                          >
                            {errors.message.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      startIcon={<Iconify icon="ic:outline-remove-red-eye" />}
                      onClick={() => setShowEmailPreview(true)}
                    >
                      Preview
                    </Button>

                    <Button
                      type="submit"
                      form="bulk-email-compose-form"
                      color="primary"
                      variant="contained"
                      startIcon={<Iconify icon="ic:outline-send" />}
                      disabled={formSubmitted || !selectedRecipients.length}
                      sx={{ marginRight: 2 }}
                    >
                      Send to {selectedRecipients.length} Recipients
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </form>

      {showEmailPreview && (
        <ComposeEmailPreview
          title="Email Preview"
          data={{
            fromName: getValues('fromName'),
            fromEmail: getValues('fromEmail'),
            subject: getValues('subject'),
            message: getValues('message'),
          }}
          onCancel={() => setShowEmailPreview(false)}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

export default BulkEmailCompose;
